import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, untracked, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { PairDto, PairsControllerService } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY_ASSET_LABEL } from 'app/dashboard/components/trading/shared/constants';
import { isDefined, Maybe } from '@stillmandigital/common';
import { pipe, switchMap } from 'rxjs';
import { SELECTED_PAIR_KEY } from '../dashboard.constants';
import { PairPrice, PairViewModel } from '../dashboard.interfaces';

export const usePairs = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _router: type<Router>(),
      },
    },
    withState({
      pairs: new Array<PairDto>(),
      pairPrices: new Array<PairPrice>(),
      selectedPair: undefined as Maybe<PairViewModel>,
    }),
    withProps(() => ({
      _pairsService: inject(PairsControllerService),
    })),
    withComputed(store => {
      const pairViewModels = computed<PairViewModel[]>(() => {
        const pairPrices = store.pairPrices();

        return store.pairs().map(pair => ({
          ...pair,
          price: pairPrices.find(price => price.name === pair.name),
          baseAsset: pair.name.split('/')[0],
          quoteAsset: pair.name.split('/')[1],
        }));
      });

      const selectedPairAssets = computed(
        () =>
          store.selectedPair()?.name.split('/') || [EMPTY_ASSET_LABEL, EMPTY_ASSET_LABEL],
      );

      const baseAssetName = computed(() => selectedPairAssets()[0]);

      const quoteAssetName = computed(() => selectedPairAssets()[1]);

      return { pairViewModels, baseAssetName, quoteAssetName };
    }),
    withMethods(store => {
      const setPairPrices = (pairPrices: PairPrice[]) =>
        patchState(store, { pairPrices });

      // The name check stops the URL write from looping back through the route-driven
      // pair selection in DashboardComponent.
      const setSelectedPair = (selectedPair: Maybe<PairViewModel>) => {
        const name = selectedPair?.name;
        const unchanged = name === untracked(() => store.selectedPair()?.name);

        patchState(store, { selectedPair });

        if (!isDefined(name) || unchanged) {
          return;
        }

        localStorage.setItem(SELECTED_PAIR_KEY, name);
        store._router.navigate([], {
          queryParams: { pair: name },
          queryParamsHandling: 'merge',
        });
      };

      const loadPairs = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._pairsService.getPairs(store._activeAccountId()).pipe(
              tapResponse({
                next: (pairs: PairDto[]) => patchState(store, { pairs }),
                error: (_error: HttpErrorResponse) => undefined,
              }),
            ),
          ),
        ),
      );

      return { setPairPrices, setSelectedPair, loadPairs };
    }),
  );
